import { FC, useState } from 'react'
import { Box, Checkbox, FormControlLabel } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { PrintOutlined } from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { clarity } from 'react-microsoft-clarity'
import { getApiErrorMessage, sanitizeHtml } from 'utils'
import {
  InsuranceConsentResponse,
  useConfirmInsuranceConsent
} from 'services/insurance/insurance.hooks'

const setClarityEvent = (name: string) => {
  if (clarity.hasStarted()) {
    clarity.setEvent(name)
  }
}

interface ConsentActionsProps {
  consent: InsuranceConsentResponse
}

const ConsentActions: FC<ConsentActionsProps> = ({ consent }) => {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { confirmConsent, isConfirming } = useConfirmInsuranceConsent()
  const [hasReadStatement, setHasReadStatement] = useState(false)

  const handleConfirm = async () => {
    setClarityEvent('insurance_consent_confirm_attempt')

    try {
      await confirmConsent(consent.id)
      setClarityEvent('insurance_consent_confirmed')
      enqueueSnackbar(
        formatMessage({
          id: 'insuranceConsent.success',
          defaultMessage: 'Zgoda została pomyślnie potwierdzona'
        }),
        { variant: 'success' }
      )
    } catch (error: any) {
      setClarityEvent('insurance_consent_error')
      const status = error?.response?.status

      if (status === 400) {
        // Already confirmed elsewhere - refetch to pull the current status.
        queryClient.invalidateQueries(['insuranceConsents'])
        enqueueSnackbar(
          formatMessage({
            id: 'insuranceConsent.alreadyConfirmed',
            defaultMessage: 'Ta zgoda została już potwierdzona'
          }),
          { variant: 'error' }
        )
        return
      }

      if (status === 404) {
        enqueueSnackbar(
          formatMessage({
            id: 'insuranceConsent.notFound',
            defaultMessage: 'Nie znaleziono zgody'
          }),
          { variant: 'error' }
        )
        return
      }

      enqueueSnackbar(
        getApiErrorMessage(error) ||
          formatMessage({
            id: 'insuranceConsent.error',
            defaultMessage: 'Wystąpił błąd. Spróbuj ponownie później'
          }),
        { variant: 'error' }
      )
    }
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')

    if (!printWindow) {
      return
    }

    const title = formatMessage({
      id: 'insuranceConsent.title',
      defaultMessage: 'Potwierdzenie zgody na ubezpieczenie'
    })
    const contractNumberLabel = formatMessage({
      id: 'insuranceConsent.contractNumber',
      defaultMessage: 'Numer umowy'
    })
    const confirmedAtLabel = formatMessage({
      id: 'insuranceConsent.confirmedAt',
      defaultMessage: 'Data potwierdzenia'
    })

    const content = sanitizeHtml(`<h1>${title}</h1>
    <div class="meta">
      ${!!consent.leaseContractNumber ? `<div>${contractNumberLabel}: ${consent.leaseContractNumber}</div>` : ''}
      ${!!consent.confirmedAt ? `<div>${confirmedAtLabel}: ${dayjs(consent.confirmedAt).format('DD.MM.YYYY HH:mm')}</div>` : ''}
    </div>
    ${consent.statement}`)

    printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; max-width: 800px; margin: 2rem auto; padding: 0 1rem; line-height: 1.6; color: #222; }
      .meta { color: #555; margin-bottom: 2rem; }
    </style>
  </head>
  <body>
    ${content}
  </body>
</html>`)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
  }

  if (consent.isConfirmed) {
    return (
      <Box sx={{ mt: 3 }}>
        <LoadingButton
          variant='outlined'
          fullWidth
          size='large'
          startIcon={<PrintOutlined />}
          onClick={handlePrint}>
          {formatMessage({
            id: 'insuranceConsent.print',
            defaultMessage: 'Drukuj / zapisz potwierdzenie'
          })}
        </LoadingButton>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 3 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={hasReadStatement}
            onChange={({ target }) => setHasReadStatement(target.checked)}
            disabled={isConfirming}
          />
        }
        label={formatMessage({
          id: 'insuranceConsent.readAndUnderstood',
          defaultMessage: 'Przeczytałem i rozumiem treść oświadczenia'
        })}
        sx={{ mb: 2 }}
      />
      <LoadingButton
        variant='contained'
        fullWidth
        size='large'
        sx={{ py: 1.5 }}
        disabled={!hasReadStatement}
        loading={isConfirming}
        loadingIndicator={formatMessage({
          id: 'insuranceConsent.processing',
          defaultMessage: 'Przetwarzanie...'
        })}
        onClick={handleConfirm}>
        {formatMessage({
          id: 'insuranceConsent.confirmButton',
          defaultMessage: 'Potwierdzam i akceptuję'
        })}
      </LoadingButton>
    </Box>
  )
}

export default ConsentActions
